"use client";
import {createContext, useContext, useEffect, useState} from "react";
import {FCWithChildren} from "../../types/reactTypes";
import UserInfo from "../../types/UserInfo";
import {fetchUserInfoClient} from "@/lib/legacyApi/fetchUserInfoClient";

interface UserInfoContextValue {
	userInfo: UserInfo | null;
	isLoading: boolean;
}

/**
 * Context and helpers for getting the user info via a React context.
 */
const UserInfoContext = createContext<UserInfoContextValue>({
	userInfo: null,
	isLoading: false,
});

export default UserInfoContext;

export const useUserInfo = () => {
	return useContext(UserInfoContext);
};

export const useIsLoggedIn = () => {
	const {userInfo} = useContext(UserInfoContext);
	return !!userInfo;
};

export const UserInfoProvider = UserInfoContext.Provider;

// This wrapper is necessary even though it doesn't seem to do anything because
// UserInfoProvider cannot be used inside server components, for some reason.
export const UserInfoWrapperClient: FCWithChildren = ({children}) => {
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
	useEffect(() => {
		(async () => {
			const user = await fetchUserInfoClient();
			setUserInfo(user);
			setIsLoading(false);
		})();
	}, []);

	return (
		<UserInfoProvider value={{userInfo, isLoading}}>
			{children}
		</UserInfoProvider>
	);
};
